import { KeyRound, Trash2 } from "lucide-react";
import { useT } from "@/src/i18n";
import { orphanedRoomKeys, roomKeySnippet } from "@/src/core/roomKeys";
import { useMapStore } from "@/src/store/useMapStore";

/**
 * Minimal discoverability for keys whose room number was deleted.
 *
 * It lives inside the Label drawer because that is the tool that creates room
 * numbers in the first place, and because a per-room panel cannot be the only
 * home for orphans — once the last annotation is gone there is nothing left to
 * double-click. Re-linking an orphan to another annotation is out of scope for
 * Slice A; see FEATURE_GAP.md (D-025).
 */
export function OrphanedRoomKeys() {
  const roomKeys = useMapStore((state) => state.roomKeys);
  const objects = useMapStore((state) => state.objects);
  const deleteRoomKey = useMapStore((state) => state.deleteRoomKey);
  const openRoomKeyPanel = useMapStore((state) => state.openRoomKeyPanel);
  const t = useT();

  const orphans = orphanedRoomKeys(roomKeys, objects);
  if (orphans.length === 0) return null;

  return (
    <section className="border-t border-white/8 px-3 py-3" data-testid="orphaned-room-keys">
      <p className="mb-1 flex items-center gap-1.5 px-1 text-[9px] font-bold uppercase tracking-[0.18em] text-stone-600">
        <KeyRound size={11} /> {t("roomKey.orphanListTitle")}
      </p>
      <p className="mb-2 px-1 text-[9px] leading-4 text-stone-600">{t("roomKey.orphanListHint")}</p>
      <ul className="space-y-1.5">
        {orphans.map(({ objectId, key }) => {
          const name = key.name?.trim() || t("roomKey.untitled");
          const snippet = roomKeySnippet(key);
          return (
            <li
              key={objectId}
              className="flex items-start gap-1.5 rounded-xl border border-white/6 bg-white/[0.025] px-2.5 py-2"
            >
              <button
                type="button"
                onClick={() => openRoomKeyPanel(objectId)}
                className="min-w-0 flex-1 text-start"
                aria-label={t("roomKey.openOrphan", { name })}
              >
                <span className="block truncate text-[11px] font-semibold text-stone-100">{name}</span>
                <span className="mt-0.5 block truncate text-[9px] text-stone-500">
                  {snippet || t("roomKey.emptySnippet")}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(t("roomKey.deleteOrphanConfirm", { name }))) {
                    deleteRoomKey(objectId);
                  }
                }}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-stone-400 transition hover:bg-red-500/15 hover:text-red-300"
                aria-label={t("roomKey.deleteOrphan", { name })}
                data-orphan-delete={objectId}
              >
                <Trash2 size={12} />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
